/**
 * Customer Review Display System
 * Fetches and displays product reviews from Spring Boot backend
 */
class ReviewDisplay {
  constructor(options = {}) {
    this.apiUrl = options.apiUrl || window.reviewSystem?.apiUrl || '';
    this.productId = options.productId || window.reviewSystem?.productId || '';
    this.container = document.getElementById(options.containerId || 'reviews-list');
    this.summaryContainer = document.getElementById(options.summaryId || 'reviews-summary');
    
    // Pagination settings
    this.currentPage = 1;
    this.reviewsPerPage = options.reviewsPerPage || 5;
    this.totalReviews = 0;
    
    // State management
    this.reviews = [];
    this.stats = null;
    this.isLoading = false;
    this.sortOrder = 'newest'; // newest, oldest, highest, lowest
    
    // Initialize if containers exist
    if (this.container && this.productId) {
      this.init();
    }
  }

  async init() {
    try {
      this.showLoading();
      await this.loadReviewStats();
      await this.loadReviews();
      this.renderSummary();
      this.renderReviews();
      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to initialize review display:', error);
      this.showError('Unable to load reviews. Please try again later.');
    }
  }

  async loadReviewStats() {
    try {
      const response = await fetch(`${this.apiUrl}/api/v1/reviews/products/${this.productId}/stats`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      this.stats = await response.json();
    } catch (error) {
      console.error('Failed to load review stats:', error);
      this.stats = this.getDefaultStats();
    }
  }

  async loadReviews(page = 1) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();

    try {
      const params = new URLSearchParams({
        page: page - 1, // Backend uses 0-based indexing
        size: this.reviewsPerPage,
        sort: this.sortOrder
      });

      const response = await fetch(`${this.apiUrl}/api/v1/reviews/products/${this.productId}?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      
      if (page === 1) {
        this.reviews = data.content || data;
      } else {
        this.reviews = [...this.reviews, ...(data.content || data)];
      }
      
      this.totalReviews = data.totalElements || this.reviews.length;
      this.currentPage = page;

    } catch (error) {
      console.error('Failed to load reviews:', error);
      if (page === 1) {
        this.showError('Unable to load reviews. Please try again later.');
      }
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  renderSummary() {
    if (!this.summaryContainer || !this.stats) return;

    const averageRating = this.stats.averageRating || 0;
    const totalReviews = this.stats.totalReviews || 0;
    
    this.summaryContainer.innerHTML = `
      <div class="reviews-summary">
        <div class="reviews-summary__rating">
          <div class="star-rating star-rating--display" data-rating="${averageRating}">
            ${this.renderStars(averageRating)}
          </div>
          <span class="reviews-summary__rating-text">
            ${averageRating.toFixed(1)} out of 5 stars
          </span>
        </div>
        <div class="reviews-summary__count">
          Based on ${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}
        </div>
        ${this.renderRatingDistribution()}
      </div>
    `;
  }

  renderRatingDistribution() {
    if (!this.stats?.ratingBreakdown) return '';

    const distribution = this.stats.ratingBreakdown;
    const total = this.stats.totalReviews || 1;

    return `
      <div class="rating-distribution">
        ${[5, 4, 3, 2, 1].map(rating => {
          const count = distribution[rating] || 0;
          const percentage = ((count / total) * 100).toFixed(1);
          
          return `
            <div class="rating-distribution__row">
              <span class="rating-distribution__label">${rating} star</span>
              <div class="rating-distribution__bar">
                <div class="rating-distribution__fill" style="width: ${percentage}%"></div>
              </div>
              <span class="rating-distribution__count">${count}</span>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  renderReviews() {
    if (!this.container) return;

    if (this.reviews.length === 0) {
      this.container.innerHTML = `
        <div class="reviews-empty">
          <p>No reviews yet. Be the first to share your experience!</p>
        </div>
      `;
      return;
    }

    const reviewsHtml = this.reviews.map(review => this.renderReview(review)).join('');
    
    this.container.innerHTML = `
      <div class="reviews-header">
        <h3>Customer Reviews</h3>
        <div class="reviews-sort">
          <label for="review-sort">Sort by:</label>
          <select id="review-sort" class="reviews-sort__select">
            <option value="newest" ${this.sortOrder === 'newest' ? 'selected' : ''}>Newest first</option>
            <option value="oldest" ${this.sortOrder === 'oldest' ? 'selected' : ''}>Oldest first</option>
            <option value="highest" ${this.sortOrder === 'highest' ? 'selected' : ''}>Highest rated</option>
            <option value="lowest" ${this.sortOrder === 'lowest' ? 'selected' : ''}>Lowest rated</option>
          </select>
        </div>
      </div>
      <div class="reviews-list">
        ${reviewsHtml}
      </div>
      ${this.renderPagination()}
    `;
  }

  renderReview(review) {
    const reviewDate = new Date(review.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const verifiedBadge = review.verified ? 
      '<span class="review-verified">✓ Verified Purchase</span>' : '';

    return `
      <article class="review-card" data-review-id="${review.id}">
        <div class="review-card__header">
          <div class="review-card__rating">
            <div class="star-rating star-rating--display" data-rating="${review.rating}">
              ${this.renderStars(review.rating)}
            </div>
          </div>
          <div class="review-card__meta">
            <span class="review-card__author">${this.escapeHtml(review.customerName)}</span>
            <span class="review-card__date">${reviewDate}</span>
            ${verifiedBadge}
          </div>
        </div>
        
        ${review.title ? `<h4 class="review-card__title">${this.escapeHtml(review.title)}</h4>` : ''}
        
        <div class="review-card__content">
          <p>${this.escapeHtml(review.content)}</p>
        </div>
        
        ${this.renderReviewActions(review)}
      </article>
    `;
  }

  renderReviewActions(review) {
    const helpfulCount = review.helpfulVotes || 0;
    
    return `
      <div class="review-actions">
        <button 
          class="review-helpful-btn" 
          data-review-id="${review.id}"
          aria-label="Mark this review as helpful"
        >
          <span class="review-helpful-icon">👍</span>
          <span class="review-helpful-text">
            Helpful ${helpfulCount > 0 ? `(${helpfulCount})` : ''}
          </span>
        </button>
      </div>
    `;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.totalReviews / this.reviewsPerPage);
    
    if (totalPages <= 1) return '';

    const hasMore = this.currentPage < totalPages;
    
    return `
      <div class="reviews-pagination">
        <div class="reviews-pagination__info">
          Showing ${this.reviews.length} of ${this.totalReviews} reviews
        </div>
        ${hasMore ? `
          <button 
            class="reviews-load-more" 
            data-page="${this.currentPage + 1}"
            aria-label="Load more reviews"
          >
            Load More Reviews
          </button>
        ` : ''}
      </div>
    `;
  }

  renderStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return `
      ${'★'.repeat(fullStars)}
      ${hasHalfStar ? '☆' : ''}
      ${'☆'.repeat(emptyStars)}
    `;
  }

  setupEventListeners() {
    // Sort functionality
    const sortSelect = document.getElementById('review-sort');
    if (sortSelect) {
      sortSelect.addEventListener('change', async (e) => {
        this.sortOrder = e.target.value;
        this.currentPage = 1;
        this.reviews = [];
        await this.loadReviews();
        this.renderReviews();
      });
    }

    // Load more functionality
    this.container.addEventListener('click', async (e) => {
      if (e.target.matches('.reviews-load-more')) {
        const nextPage = parseInt(e.target.dataset.page);
        await this.loadReviews(nextPage);
        this.renderReviews();
      }

      // Helpful vote functionality
      if (e.target.matches('.review-helpful-btn, .review-helpful-btn *')) {
        const btn = e.target.closest('.review-helpful-btn');
        const reviewId = btn.dataset.reviewId;
        await this.voteHelpful(reviewId, btn);
      }
    });
  }

  async voteHelpful(reviewId, button) {
    try {
      button.disabled = true;
      
      const response = await fetch(`${this.apiUrl}/api/v1/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Update local data
      const review = this.reviews.find(r => r.id === reviewId);
      if (review) {
        review.helpfulVotes = (review.helpfulVotes || 0) + 1;
        
        // Update button display
        const countSpan = button.querySelector('.review-helpful-text');
        countSpan.textContent = `Helpful (${review.helpfulVotes})`;
        button.classList.add('review-helpful-btn--voted');
      }

    } catch (error) {
      console.error('Failed to vote helpful:', error);
    } finally {
      button.disabled = false;
    }
  }

  showLoading() {
    if (this.container && this.reviews.length === 0) {
      this.container.innerHTML = `
        <div class="reviews-loading">
          <div class="reviews-loading__spinner"></div>
          <p>Loading reviews...</p>
        </div>
      `;
    }
  }

  hideLoading() {
    const loader = this.container?.querySelector('.reviews-loading');
    if (loader) {
      loader.remove();
    }
  }

  showError(message) {
    if (this.container) {
      this.container.innerHTML = `
        <div class="reviews-error">
          <p>${message}</p>
          <button class="reviews-retry-btn">Try Again</button>
        </div>
      `;

      // Add retry functionality
      const retryBtn = this.container.querySelector('.reviews-retry-btn');
      retryBtn?.addEventListener('click', () => this.init());
    }
  }

  getDefaultStats() {
    return {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    };
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Check if we're on a product page with review containers
  if (document.getElementById('reviews-list') && window.reviewSystem) {
    window.reviewDisplay = new ReviewDisplay();
  }
});

// Export for manual initialization
window.ReviewDisplay = ReviewDisplay;